import { ErrorDetector, IErrorDetectorConfig, IErrorDetails } from './ErrorDetector';
import { WebSocketConnector, IWebSocketConnectorMessage } from '../network-interceptor/WebSocketConnector';
import { IDebugEvent } from '../types';

export interface IRealTimeErrorReporterConfig extends Partial<IErrorDetectorConfig> {
  websocket?: {
    url: string;
    reconnectInterval?: number;
    maxReconnectAttempts?: number;
    enableHeartbeat?: boolean;
    heartbeatInterval?: number;
  };
  enableStateSnapshot?: boolean;
  enableConsoleContext?: boolean;
  enableNetworkContext?: boolean;
  enablePerformanceContext?: boolean;
  autoConnect?: boolean;
  bufferErrors?: boolean;
  maxErrorBuffer?: number;
  enableLogging?: boolean;
  onError?: (event: IDebugEvent) => void;
}

/**
 * 실시간 에러 리포터 - 에러를 감지하고 즉시 WebSocket으로 전송
 */
export class RealTimeErrorReporter {
  private _config: Required<Omit<IRealTimeErrorReporterConfig, 'onError'>> & {
    onError?: (event: IDebugEvent) => void;
  };
  private _errorDetector: ErrorDetector;
  private _webSocketConnector: WebSocketConnector | null = null;
  private _isActive: boolean = false;
  private _errorBuffer: IDebugEvent[] = [];
  private _stats = {
    totalErrors: 0,
    sentErrors: 0,
    bufferedErrors: 0,
    startTime: 0,
  };

  constructor(config: IRealTimeErrorReporterConfig = {}) {
    this._config = {
      // ErrorDetector 기본 설정
      captureConsole: true,
      captureUnhandledErrors: true,
      captureUnhandledRejections: true,
      captureReactErrors: true,
      includeSourceMap: false,
      maxStackFrames: 50,
      excludePatterns: [
        /chrome-extension:/,
        /moz-extension:/,
        /webkit-masked-url:/,
        /^Script error\.?$/,
        /Non-Error promise rejection captured/,
        /sockjs/,
        /websocket/,
      ],
      onError: undefined,
      
      // RealTimeErrorReporter 설정
      websocket: {
        url: 'ws://localhost:8080/ws',
        reconnectInterval: 3000,
        maxReconnectAttempts: 10,
        enableHeartbeat: true,
        heartbeatInterval: 30000,
        ...config.websocket,
      },
      enableStateSnapshot: true,
      enableConsoleContext: true,
      enableNetworkContext: true,
      enablePerformanceContext: true,
      autoConnect: true,
      bufferErrors: true,
      maxErrorBuffer: 100,
      enableLogging: true,
      
      ...config,
    };

    // ErrorDetector 초기화
    this._errorDetector = new ErrorDetector({
      ...this._config,
      onError: this._handleError.bind(this),
    });

    // WebSocketConnector 초기화
    if (this._config.websocket.url) {
      this._webSocketConnector = new WebSocketConnector(this._config.websocket);
    }

    this._log('RealTimeErrorReporter initialized');
  }

  /**
   * 실시간 에러 리포팅 시작
   */
  public async start(): Promise<void> {
    if (this._isActive) {
      this._log('Already active');
      return;
    }

    this._log('Starting real-time error reporting...');
    this._stats.startTime = Date.now();

    try {
      // WebSocket 연결 먼저 시도 (autoConnect가 true인 경우)
      if (this._webSocketConnector && this._config.autoConnect) {
        try {
          await this._webSocketConnector.connect();
          this._log('WebSocket connected successfully');
          
          // 버퍼된 에러들 전송
          await this._flushErrorBuffer();
        } catch (error) {
          this._log('WebSocket connection failed, errors will be buffered:', error);
        }
      }

      // 에러 감지 시작
      this._errorDetector.start();
      this._log('Error detection started');

      this._isActive = true;
      this._log('Real-time error reporting active');

      // 시작 이벤트 전송
      this._sendStartEvent();

    } catch (error) {
      this._log('Failed to start real-time error reporting:', error);
      throw error;
    }
  }

  /**
   * 실시간 에러 리포팅 중지
   */
  public async stop(): Promise<void> {
    if (!this._isActive) {
      return;
    }

    this._log('Stopping real-time error reporting...');

    try {
      // 에러 감지 중지
      this._errorDetector.stop();
      this._log('Error detection stopped');

      // 종료 이벤트 전송
      this._sendStopEvent();

      // 남은 에러들 전송
      await this._flushErrorBuffer();

      // WebSocket 연결 해제
      if (this._webSocketConnector) {
        this._webSocketConnector.disconnect();
        this._log('WebSocket disconnected');
      }

      this._isActive = false;
      this._log('Real-time error reporting stopped');

    } catch (error) {
      this._log('Error while stopping real-time error reporting:', error);
      throw error;
    }
  }

  /**
   * WebSocket 수동 연결
   */
  public async connectWebSocket(): Promise<void> {
    if (!this._webSocketConnector) {
      throw new Error('WebSocket connector not configured');
    }

    await this._webSocketConnector.connect();
    this._log('WebSocket manually connected');
    
    // 연결 후 버퍼된 에러들 전송
    await this._flushErrorBuffer();
  }

  /**
   * WebSocket 연결 해제
   */
  public disconnectWebSocket(): void {
    if (this._webSocketConnector) {
      this._webSocketConnector.disconnect();
      this._log('WebSocket manually disconnected');
    }
  }

  /**
   * 에러 수동 리포트
   */
  public reportError(error: Error, context?: Record<string, unknown>): void {
    const errorEvent: IDebugEvent = {
      id: `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'error',
      data: {
        id: `error_${Date.now()}`,
        type: 'error',
        message: error.message,
        stack: error.stack,
        source: 'manual',
        level: 'error',
        context: {
          ...this._errorDetector.getCurrentContext(),
          ...context,
          originalError: error,
        },
        originalError: error,
      } as IErrorDetails,
      timestamp: Date.now(),
      source: 'RealTimeErrorReporter',
    };

    this._handleError(errorEvent);
  }

  /**
   * 브레드크럼 추가
   */
  public addBreadcrumb(breadcrumb: {
    type: 'navigation' | 'user_action' | 'http' | 'console' | 'state_change';
    message: string;
    data?: Record<string, unknown>;
    level?: 'info' | 'warning' | 'error';
  }): void {
    this._errorDetector.addBreadcrumb({
      ...breadcrumb,
      level: breadcrumb.level || 'info',
    });
  }

  /**
   * 통계 정보 가져오기
   */
  public getStats() {
    const uptime = Date.now() - this._stats.startTime;

    return {
      ...this._stats,
      uptime,
      isActive: this._isActive,
      webSocketConnected: this._webSocketConnector?.isConnected || false,
      errorDetectorState: this._errorDetector.getState(),
    };
  }

  /**
   * 현재 상태 정보
   */
  public getState() {
    return {
      isActive: this._isActive,
      config: { ...this._config },
      stats: this.getStats(),
      errorDetector: this._errorDetector.getState(),
      webSocketConnector: this._webSocketConnector?.getState(),
      errorBufferSize: this._errorBuffer.length,
    };
  }

  /**
   * 에러 이벤트 처리
   */
  private _handleError(errorEvent: IDebugEvent): void {
    this._stats.totalErrors++;

    this._log(`🔴 Error detected: ${(errorEvent.data as IErrorDetails).message}`, {
      id: errorEvent.id,
      source: (errorEvent.data as IErrorDetails).source,
      level: (errorEvent.data as IErrorDetails).level,
    });

    // 추가 컨텍스트 정보 수집
    const enhancedErrorEvent = this._enhanceErrorEvent(errorEvent);

    // WebSocket으로 즉시 전송 또는 버퍼에 저장
    if (this._webSocketConnector?.isConnected) {
      this._sendErrorEvent(enhancedErrorEvent);
      this._stats.sentErrors++;
    } else if (this._config.bufferErrors) {
      this._bufferErrorEvent(enhancedErrorEvent);
    }
  }

  /**
   * 에러 이벤트 강화 (추가 컨텍스트 수집)
   */
  private _enhanceErrorEvent(errorEvent: IDebugEvent): IDebugEvent {
    const errorDetails = errorEvent.data as IErrorDetails;
    const enhancedContext = { ...errorDetails.context };

    // 성능 컨텍스트 추가
    if (this._config.enablePerformanceContext) {
      enhancedContext.performance = this._collectPerformanceContext();
    }

    // 네트워크 컨텍스트 추가 (간단한 버전)
    if (this._config.enableNetworkContext) {
      enhancedContext.network = this._collectNetworkContext();
    }

    // 상태 스냅샷 (간단한 버전)
    if (this._config.enableStateSnapshot) {
      enhancedContext.stateSnapshot = this._collectStateSnapshot();
    }

    return {
      ...errorEvent,
      data: {
        ...errorDetails,
        context: enhancedContext,
      },
    };
  }

  /**
   * 성능 컨텍스트 수집
   */
  private _collectPerformanceContext() {
    if (typeof performance === 'undefined') {
      return null;
    }

    try {
      return {
        memory: (performance as any).memory ? {
          usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
          totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
        } : null,
        navigation: performance.getEntriesByType ? 
          performance.getEntriesByType('navigation')[0] : null,
        timing: performance.timing ? {
          loadEventEnd: performance.timing.loadEventEnd,
          domContentLoadedEventEnd: performance.timing.domContentLoadedEventEnd,
          connectEnd: performance.timing.connectEnd,
          connectStart: performance.timing.connectStart,
        } : null,
      };
    } catch (error) {
      return { error: (error as Error).toString() };
    }
  }

  /**
   * 네트워크 컨텍스트 수집
   */
  private _collectNetworkContext() {
    if (typeof navigator === 'undefined') {
      return null;
    }

    try {
      return {
        online: navigator.onLine,
        connection: (navigator as any).connection ? {
          effectiveType: (navigator as any).connection.effectiveType,
          downlink: (navigator as any).connection.downlink,
          rtt: (navigator as any).connection.rtt,
        } : null,
      };
    } catch (error) {
      return { error: (error as Error).toString() };
    }
  }

  /**
   * 상태 스냅샷 수집 (간단한 버전)
   */
  private _collectStateSnapshot() {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return {
        localStorage: this._getLocalStorageSnapshot(),
        sessionStorage: this._getSessionStorageSnapshot(),
        cookies: document.cookie ? document.cookie.split(';').length : 0,
        url: window.location.href,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
          scrollX: window.scrollX,
          scrollY: window.scrollY,
        },
      };
    } catch (error) {
      return { error: (error as Error).toString() };
    }
  }

  /**
   * LocalStorage 스냅샷 (키만)
   */
  private _getLocalStorageSnapshot() {
    try {
      if (typeof localStorage === 'undefined') {
        return null;
      }
      
      const keys = Object.keys(localStorage);
      return {
        keyCount: keys.length,
        keys: keys.slice(0, 10), // 최대 10개 키만
      };
    } catch (error) {
      return { error: (error as Error).toString() };
    }
  }

  /**
   * SessionStorage 스냅샷 (키만)
   */
  private _getSessionStorageSnapshot() {
    try {
      if (typeof sessionStorage === 'undefined') {
        return null;
      }
      
      const keys = Object.keys(sessionStorage);
      return {
        keyCount: keys.length,
        keys: keys.slice(0, 10), // 최대 10개 키만
      };
    } catch (error) {
      return { error: (error as Error).toString() };
    }
  }

  /**
   * 에러 이벤트 전송
   */
  private _sendErrorEvent(errorEvent: IDebugEvent): void {
    if (!this._webSocketConnector?.isConnected) {
      this._log('Cannot send error event: WebSocket not connected');
      return;
    }

    try {
      this._webSocketConnector.sendError(
        new Error((errorEvent.data as IErrorDetails).message),
        errorEvent.data as any
      );
    } catch (error) {
      this._log('Failed to send error event:', error);
      
      // 전송 실패한 경우 버퍼에 저장
      if (this._config.bufferErrors) {
        this._bufferErrorEvent(errorEvent);
      }
    }
  }

  /**
   * 에러 이벤트 버퍼링
   */
  private _bufferErrorEvent(errorEvent: IDebugEvent): void {
    this._errorBuffer.push(errorEvent);
    this._stats.bufferedErrors++;

    // 버퍼 크기 제한
    if (this._errorBuffer.length > this._config.maxErrorBuffer) {
      this._errorBuffer = this._errorBuffer.slice(-this._config.maxErrorBuffer);
    }

    this._log(`Error buffered. Buffer size: ${this._errorBuffer.length}`);
  }

  /**
   * 에러 버퍼 전송
   */
  private async _flushErrorBuffer(): Promise<void> {
    if (this._errorBuffer.length === 0) {
      return;
    }

    if (!this._webSocketConnector?.isConnected) {
      this._log('Cannot flush error buffer: WebSocket not connected');
      return;
    }

    this._log(`Flushing ${this._errorBuffer.length} buffered errors`);

    const errors = [...this._errorBuffer];
    this._errorBuffer = [];

    for (const errorEvent of errors) {
      try {
        this._sendErrorEvent(errorEvent);
        this._stats.sentErrors++;
        this._stats.bufferedErrors--;
        
        // 전송 간격 (과부하 방지)
        await new Promise(resolve => setTimeout(resolve, 10));
      } catch (error) {
        this._log('Failed to send buffered error:', error);
        // 실패한 에러는 다시 버퍼에 저장
        this._errorBuffer.unshift(errorEvent);
        break;
      }
    }
  }

  /**
   * 시작 이벤트 전송
   */
  private _sendStartEvent(): void {
    if (this._webSocketConnector?.isConnected) {
      const startEvent: IDebugEvent = {
        id: `error_reporter_start_${Date.now()}`,
        type: 'info',
        data: {
          message: 'RealTimeErrorReporter started',
          config: this._config,
          timestamp: Date.now(),
        },
        timestamp: Date.now(),
        source: 'RealTimeErrorReporter',
      };

      this._sendErrorEvent(startEvent);
    }
  }

  /**
   * 종료 이벤트 전송
   */
  private _sendStopEvent(): void {
    if (this._webSocketConnector?.isConnected) {
      const stopEvent: IDebugEvent = {
        id: `error_reporter_stop_${Date.now()}`,
        type: 'info',
        data: {
          message: 'RealTimeErrorReporter stopped',
          stats: this.getStats(),
          timestamp: Date.now(),
        },
        timestamp: Date.now(),
        source: 'RealTimeErrorReporter',
      };

      this._sendErrorEvent(stopEvent);
    }
  }

  /**
   * 로깅 헬퍼
   */
  private _log(message: string, data?: unknown): void {
    if (!this._config.enableLogging) {
      return;
    }

    const timestamp = new Date().toISOString();
    console.log(`[RealTimeErrorReporter] ${timestamp} ${message}`, data || '');
  }

  /**
   * 정리
   */
  public async destroy(): Promise<void> {
    this._log('Destroying RealTimeErrorReporter...');
    
    await this.stop();
    
    this._errorBuffer = [];
    
    this._log('RealTimeErrorReporter destroyed');
  }
}

// 전역 인스턴스 (선택적)
let globalErrorReporter: RealTimeErrorReporter | null = null;

/**
 * 전역 에러 리포터 생성/가져오기
 */
export function getGlobalErrorReporter(config?: IRealTimeErrorReporterConfig): RealTimeErrorReporter {
  if (!globalErrorReporter) {
    globalErrorReporter = new RealTimeErrorReporter(config);
  }
  return globalErrorReporter;
}

/**
 * 전역 에러 리포터 정리
 */
export async function destroyGlobalErrorReporter(): Promise<void> {
  if (globalErrorReporter) {
    await globalErrorReporter.destroy();
    globalErrorReporter = null;
  }
}

/**
 * React Error Boundary 컴포넌트 생성 헬퍼
 */
export function createErrorBoundaryWithReporter(errorReporter: RealTimeErrorReporter) {
  // React 동적 import 시도
  let React: any;
  try {
    React = require('react');
  } catch (e) {
    console.warn('[RealTimeErrorReporter] React not available for Error Boundary');
    return null;
  }

  return class TimeMachineErrorBoundary extends React.Component<any, any> {
    constructor(props: any) {
      super(props);
      this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error) {
      return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: any) {
      // 에러 리포터에 브레드크럼 추가
      errorReporter.addBreadcrumb({
        type: 'console',
        message: `React Error Boundary caught: ${error.message}`,
        level: 'error',
        data: {
          componentStack: errorInfo.componentStack,
          error: {
            name: error.name,
            message: error.message,
            stack: error.stack,
          },
        },
      });

      // 에러 리포터에 직접 리포트
      errorReporter.reportError(error, {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
        props: this.props,
      });
    }

    render() {
      if (this.state.hasError) {
        return React.createElement(
          'div',
          {
            style: {
              padding: '20px',
              border: '2px solid #ff6b6b',
              borderRadius: '8px',
              backgroundColor: '#ffe0e0',
              color: '#c92a2a',
              fontFamily: 'monospace',
            },
          },
          React.createElement('h3', null, '⚠️ Something went wrong'),
          React.createElement('p', null, this.state.error?.message || 'Unknown error'),
          React.createElement(
            'details',
            null,
            React.createElement('summary', null, 'Error Details'),
            React.createElement('pre', {
              style: { fontSize: '12px', overflow: 'auto' }
            }, this.state.error?.stack)
          )
        );
      }

      return this.props.children;
    }
  };
}
